import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Home as HomeIcon, Tag, CheckCircle2, ChevronRight, TrendingUp } from "lucide-react";

const dayLabel = (iso) => new Date(iso).toLocaleDateString("en-AU", { weekday: "short" });

export default function HomeVisitorFunnel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/admin/visitor-stats?range=week&group=day`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => {});
  }, []);

  if (!data) return null;

  const s = data.summary || { homepage: 0, pricing_not_subscribed: 0, converted: 0, conversion_rate: 0 };
  const rows = data.breakdown || [];
  const maxVal = Math.max(1, ...rows.map((r) => Math.max(r.homepage, r.pricing_not_subscribed, r.converted)));

  const Step = ({ icon: Icon, label, value, color }) => (
    <div className="flex items-center gap-2">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="leading-tight">
        <p className="text-xl font-bold text-slate-900">{value}</p>
        <p className="text-[11px] text-slate-500">{label}</p>
      </div>
    </div>
  );

  return (
    <Card
      className="mb-6 cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => navigate("/admin/subscriptions")}
      data-testid="home-visitor-funnel"
    >
      <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
        <div className="flex items-center gap-2 shrink-0">
          <TrendingUp className="w-4 h-4 text-amber-600" />
          <div>
            <p className="font-bold text-slate-900 text-sm">Visitor Funnel</p>
            <p className="text-[11px] text-slate-400">Last 7 days</p>
          </div>
        </div>

        <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
          <Step icon={HomeIcon} label="Homepage" value={s.homepage} color="bg-blue-500" />
          <ChevronRight className="w-4 h-4 text-slate-300" />
          <Step icon={Tag} label="Pricing" value={s.pricing_not_subscribed} color="bg-purple-500" />
          <ChevronRight className="w-4 h-4 text-slate-300" />
          <div className="flex items-center gap-2">
            <Step icon={CheckCircle2} label="Converted" value={s.converted} color="bg-green-500" />
            <span className="text-[11px] font-semibold text-green-700 bg-green-100 rounded-full px-2 py-0.5">
              {s.conversion_rate}%
            </span>
          </div>
        </div>

        <div className="flex items-end gap-2 lg:ml-auto">
          {rows.slice(-7).map((r) => (
            <div key={r.period} className="flex flex-col items-center gap-1">
              <div className="flex items-end gap-0.5 h-8" title={`${dayLabel(r.period)} · H${r.homepage} P${r.pricing_not_subscribed} C${r.converted}`}>
                <span className="bg-blue-500 w-1.5 rounded-sm" style={{ height: `${(r.homepage / maxVal) * 100}%` }} />
                <span className="bg-purple-500 w-1.5 rounded-sm" style={{ height: `${(r.pricing_not_subscribed / maxVal) * 100}%` }} />
                <span className="bg-green-500 w-1.5 rounded-sm" style={{ height: `${(r.converted / maxVal) * 100}%` }} />
              </div>
              <span className="text-[9px] text-slate-400">{dayLabel(r.period)}</span>
            </div>
          ))}
          <span className="text-xs font-medium text-amber-600 hover:text-amber-700 self-center ml-2 hidden sm:inline">
            View details →
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
